import { useState } from "react";
import axios from "axios";
import { Mail, MessageCircle, Phone, Send, Loader2, CheckCircle2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CONTACT, SERVICE_OPTIONS } from "../lib/content";

// En producción (cPanel) el formulario se envía a /contact.php (mismo dominio).
// En desarrollo local, si existe REACT_APP_CONTACT_ENDPOINT se usa ese.
const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT || "/contact.php";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Completa nombre, email y mensaje para continuar.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(CONTACT_ENDPOINT, form, {
        headers: { "Content-Type": "application/json" },
      });
      if (data && data.ok === false) {
        throw new Error(data.error || "Error al enviar el mensaje.");
      }
      setSent(true);
      toast.success("¡Mensaje enviado! Te responderemos muy pronto.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
      setTimeout(() => setSent(false), 5000);
      return data;
    } catch (err) {
      const detail =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        "Error al enviar el mensaje.";
      toast.error(typeof detail === "string" ? detail : "Error al enviar el mensaje.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative bg-slate-900 text-white pt-24 md:pt-32 pb-16 overflow-hidden"
    >

      <div className="container mx-auto relative px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-orange-400 mb-4">
              <span>{`{ `}</span>contacto<span>{` }`}</span>
            </div>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tight">
              Cuéntanos tu idea.
              <br />
              <span className="text-purple-400">La hacemos realidad.</span>
            </h2>
            <p className="mt-6 text-slate-300 text-lg max-w-lg">
              Agenda una llamada sin costo o escríbenos directamente por el canal que prefieras.
              Respondemos en menos de 24 horas hábiles.
            </p>

            <ul className="mt-10 space-y-4">
              <li>
                <a
                  href={CONTACT.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="flex items-center gap-4 p-4 md:p-5 bg-slate-800/80 border border-white/10 rounded-xl hover:border-orange-400 hover:bg-slate-800 transition-all group"
                >
                  <span className="h-11 w-11 flex items-center justify-center rounded-lg bg-green-500/15 text-green-400 border border-green-500/30">
                    <MessageCircle size={20} />
                  </span>
                  <div className="flex-1">
                    <div className="text-xs font-mono uppercase tracking-widest text-slate-400">WhatsApp</div>
                    <div className="font-semibold text-white group-hover:text-orange-400 transition-colors">
                      {CONTACT.phoneDisplay}
                    </div>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={CONTACT.mailto}
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 p-4 md:p-5 bg-slate-800/80 border border-white/10 rounded-xl hover:border-purple-400 hover:bg-slate-800 transition-all group"
                >
                  <span className="h-11 w-11 flex items-center justify-center rounded-lg bg-purple-500/15 text-purple-300 border border-purple-500/30">
                    <Mail size={20} />
                  </span>
                  <div className="flex-1">
                    <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Email</div>
                    <div className="font-semibold text-white group-hover:text-purple-300 transition-colors">
                      {CONTACT.email}
                    </div>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={`tel:+${CONTACT.whatsappNumber}`}
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 p-4 md:p-5 bg-slate-800/80 border border-white/10 rounded-xl hover:border-orange-400 hover:bg-slate-800 transition-all group"
                >
                  <span className="h-11 w-11 flex items-center justify-center rounded-lg bg-orange-500/15 text-orange-400 border border-orange-500/30">
                    <Phone size={20} />
                  </span>
                  <div className="flex-1">
                    <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Teléfono</div>
                    <div className="font-semibold text-white group-hover:text-orange-400 transition-colors">
                      {CONTACT.phoneDisplay}
                    </div>
                  </div>
                </a>
              </li>
            </ul>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="bg-slate-800/80 backdrop-blur-md border border-white/10 rounded-2xl p-6 md:p-8 shadow-2xl"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-200">Nombre *</Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Tu nombre"
                  className="bg-slate-900/60 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-purple-500"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="tu@empresa.com"
                  className="bg-slate-900/60 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-purple-500"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-slate-200">Empresa</Label>
                <Input
                  id="company"
                  name="company"
                  data-testid="contact-input-company"
                  value={form.company}
                  onChange={handleChange}
                  placeholder="Nombre de tu empresa"
                  className="bg-slate-900/60 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-purple-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service" className="text-slate-200">Servicio de interés</Label>
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                >
                  <SelectTrigger
                    id="service"
                    data-testid="contact-select-service"
                    className="bg-slate-900/60 border-white/10 text-white"
                  >
                    <SelectValue placeholder="Selecciona un servicio" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} data-testid={`service-option-${opt}`}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2 mt-4">
              <Label htmlFor="message" className="text-slate-200">Mensaje *</Label>
              <Textarea
                id="message"
                name="message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={handleChange}
                placeholder="Cuéntanos sobre tu proyecto..."
                rows={5}
                className="bg-slate-900/60 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-purple-500 resize-none"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="mt-6 w-full bg-gradient-to-r from-purple-700 to-orange-600 hover:from-purple-600 hover:to-orange-500 text-white font-semibold rounded-xl h-12 text-base disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...</>
              ) : sent ? (
                <><CheckCircle2 className="mr-2 h-4 w-4" /> Enviado con éxito</>
              ) : (
                <><Send className="mr-2 h-4 w-4" /> Enviar mensaje</>
              )}
            </Button>

            <p className="mt-4 text-xs text-slate-400 font-mono">
              {`// respondemos en < 24h hábiles`}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
